import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { Image, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { searchBelgianCompany } from "@/utils/company-search";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

interface BasicInformationProps {
  name: string;
  vatNumber: string;
  assignee: string;
  logoUrl?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLogoChange?: (logoUrl: string | undefined) => void;
}

export const BasicInformation = ({ 
  name, 
  vatNumber, 
  assignee, 
  logoUrl,
  onChange,
  onLogoChange
}: BasicInformationProps) => {
  const { t } = useTranslation();
  const [isSearchingLogo, setIsSearchingLogo] = useState(false);
  const [isLogoDialogOpen, setIsLogoDialogOpen] = useState(false);
  const [logoResults, setLogoResults] = useState<{ brandId: string; name: string; icon: string; }[]>([]);
  const [selectedLogoUrl, setSelectedLogoUrl] = useState<string | null>(null);
  
  const handleLogoRemove = () => {
    if (onLogoChange) {
      onLogoChange(undefined);
    }
  };
  
  const searchLogo = async () => {
    if (!name && !vatNumber) {
      toast({
        title: "Missing Information",
        description: "A company name or VAT number is required to search for a logo.",
        variant: "destructive",
      });
      return;
    }

    setIsSearchingLogo(true);
    try {
      // Search using company name or VAT number
      const searchTerm = name || vatNumber;
      const result = await searchBelgianCompany(searchTerm);
      
      if (!result || result.logos.length === 0) {
        toast({
          title: "Logo Not Found",
          description: "No logo was found for this company.",
        });
        setIsSearchingLogo(false);
        return;
      }

      // Open dialog with logo options
      setLogoResults(result.logos);
      setIsLogoDialogOpen(true);
    } catch (error) {
      console.error("Error searching for logo:", error);
      toast({
        title: "Search Failed",
        description: "Failed to search for company logo.",
        variant: "destructive",
      });
    } finally {
      setIsSearchingLogo(false);
    }
  };

  const confirmLogoSelection = () => {
    if (selectedLogoUrl && onLogoChange) {
      onLogoChange(selectedLogoUrl);
      setIsLogoDialogOpen(false);
      setSelectedLogoUrl(null);
      toast({
        title: "Logo Selected",
        description: "Company logo has been updated successfully.",
      });
    }
  };
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          {t("companies.createModal.nameLabel")} <span className="text-red-500">*</span>
        </label>
        <Input
          id="name"
          value={name}
          onChange={onChange}
          required
        />
      </div>
      
      <div>
        <label htmlFor="vat_number" className="block text-sm font-medium text-gray-700 mb-1">
          {t("companies.createModal.vatNumberLabel")}
        </label>
        <Input
          id="vat_number"
          value={vatNumber}
          onChange={onChange}
          placeholder="BE0123456789"
        />
        <p className="text-xs text-gray-500 mt-1">Belgian VAT format: BE0123456789</p>
      </div>
      
      <div>
        <label htmlFor="assignee" className="block text-sm font-medium text-gray-700 mb-1">
          {t("companies.createModal.assigneeLabel")}
        </label>
        <Input
          id="assignee"
          value={assignee}
          onChange={onChange}
        />
      </div>
      
      {/* Logo Preview Section */}
      <div className="md:col-span-2 mt-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("companies.createModal.logoLabel", "Company Logo")}
        </label>
        <div className="flex items-start space-x-4">
          {logoUrl ? (
            <div className="relative border rounded-md p-2 bg-white w-20 h-20 flex items-center justify-center">
              <img src={logoUrl} alt="Company logo" className="max-w-full max-h-full object-contain" />
              <Button 
                variant="outline" 
                size="icon" 
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white" 
                onClick={searchLogo}
                disabled={isSearchingLogo}
                title={t("companies.search.searchForLogo", "Search for company logo")}
              >
                <Search className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="relative border rounded-md p-4 bg-gray-50 flex items-center justify-center flex-col w-20 h-20">
              <Image className="h-8 w-8 text-gray-400" />
              <span className="text-xs text-gray-500 mt-1">{t("companies.createModal.noLogo", "No Logo")}</span>
              <Button 
                variant="outline" 
                size="icon" 
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white" 
                onClick={searchLogo}
                disabled={isSearchingLogo}
                title={t("companies.search.searchForLogo", "Search for company logo")}
              >
                <Search className="h-3 w-3" />
              </Button>
            </div>
          )}
          
          {logoUrl && (
            <button 
              type="button" 
              onClick={handleLogoRemove}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              {t("companies.createModal.removeLogo", "Remove Logo")}
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {t("companies.createModal.logoSearchHelp", "Click the search icon to find logos automatically")}
        </p>
      </div>

      {/* Logo Search Dialog */}
      <Dialog open={isLogoDialogOpen} onOpenChange={setIsLogoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("companies.search.selectLogo", "Select Company Logo")}</DialogTitle>
            <DialogDescription>
              {t("companies.search.selectLogoDescription", "Choose a logo for this company")}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-60 my-4">
            <RadioGroup
              value={selectedLogoUrl || ""}
              onValueChange={setSelectedLogoUrl}
              className="grid grid-cols-2 sm:grid-cols-3 gap-3"
            >
              {logoResults.map((logo) => (
                <Label
                  key={logo.brandId}
                  htmlFor={`logo-${logo.brandId}`}
                  className={`relative flex flex-col items-center justify-center p-2 border rounded-md cursor-pointer hover:border-digger-blue ${selectedLogoUrl === logo.icon ? 'border-digger-blue ring-2 ring-digger-blue' : ''}`}
                >
                  <RadioGroupItem value={logo.icon} id={`logo-${logo.brandId}`} className="sr-only" />
                  <img src={logo.icon} alt={`${logo.name} logo`} className="h-12 w-12 object-contain mb-1" />
                  <span className="text-xs text-center truncate w-full">{logo.name}</span>
                </Label>
              ))}
            </RadioGroup>
          </ScrollArea>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsLogoDialogOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button onClick={confirmLogoSelection} disabled={!selectedLogoUrl}>
              {t("common.confirm", "Confirm")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
